import React, { useState, useEffect } from "react";
import "./AddOffers.scss";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";
import { useDocument } from "../firebase-files/hooks/useDocument";
import { useCollection } from "../firebase-files/hooks/useCollection";

const AddOffers = () => {
  const { user } = useAuthContext();

  const { documents: users } = useCollection("users");

  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [appType, setAppType] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("image");
  const [offerImage, setOfferImage] = useState(null);

  const { document: appTypeDoc } = useDocument("settings", "AppType");

  const [textOffer, setTextOffer] = useState({
    title: "",
    discount: "",
    category: "",
    product: "",
    festival: "",
    description: "",
    validFrom: "",
    validTo: "",
  });

  // 🔹 Get user role from users collection
  useEffect(() => {
    if (!user || !users) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    const phone = user?.phoneNumber?.replace("+91", "91");
    const userInfo = users.find((u) => u.id === phone);

    if (userInfo) {
      setIsAdmin(userInfo.role?.toLowerCase() === "admin");
      setIsSuperAdmin(userInfo.role?.toLowerCase() === "superadmin");
    } else {
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  }, [user, users]);

  // ✅ load app type from appTypeDoc if available
  useEffect(() => {
    if (appTypeDoc && appTypeDoc.apptype) {
      setAppType(appTypeDoc.apptype);
    }
  }, [appTypeDoc]);

  // 🔹 Permission: pro => admin/superadmin, lite => superadmin only

  const canAddOffer = () => {
    if (appType === "lite" && isSuperAdmin) return true;
    if (appType === "pro" && (isSuperAdmin || isAdmin)) return true;
    return false;
  };

  // 🔹 Handlers
  const handleTabSwitch = (tab) => setActiveTab(tab);

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setOfferImage(e.target.files[0]);
    }
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setTextOffer((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddOffer = async () => {
    try {
      const db = firebase.firestore();
      const storage = firebase.storage();

      if (activeTab === "image") {
        if (!offerImage) return;

        const newDocRef = db.collection("offers").doc();
        const docId = newDocRef.id;

        await newDocRef.set({
          type: "image",
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

        const storagePath = `offers/${docId}/${offerImage.name}`;
        const imageRef = storage.ref(storagePath);

        await imageRef.put(offerImage);
        const imageURL = await imageRef.getDownloadURL();
        await newDocRef.update({ imageURL });
      } else {
        if (!textOffer.title.trim()) return;

        const newDocRef = db.collection("offers").doc();
        await newDocRef.set({
          type: "text",
          ...textOffer,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }

      setShowModal(false);
      setOfferImage(null);
      setTextOffer({
        title: "",
        discount: "",
        category: "",
        product: "",
        festival: "",
        description: "",
        validFrom: "",
        validTo: "",
      });
      setActiveTab("image");
    } catch (error) {
      console.error("Error adding offer:", error);
    }
  };

  return (
    <div className="add-offer-container">
      {canAddOffer() && (
        <button className="open-btn" onClick={() => setShowModal(true)}>
          <span className="plus-icon">➕</span> Add Offer
        </button>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add Offer</h2>

            <div className="tabs">
              <button
                className={activeTab === "image" ? "active" : ""}
                onClick={() => handleTabSwitch("image")}
              >
                Image Based Offer
              </button>
              <button
                className={activeTab === "text" ? "active" : ""}
                onClick={() => handleTabSwitch("text")}
              >
                Text Based Offer
              </button>
            </div>

            <div className="tab-content">
              {activeTab === "image" && (
                <div className="image-tab">
                  <label htmlFor="upload-offer" className="custom-file-upload">
                    Upload Offer Image
                  </label>
                  <input
                    type="file"
                    id="upload-offer"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {offerImage && (
                    <div className="preview-image">
                      <img
                        src={URL.createObjectURL(offerImage)}
                        alt="Preview"
                      />
                    </div>
                  )}
                </div>
              )}

              {activeTab === "text" && (
                <div className="text-tab">
                  <input
                    name="title"
                    placeholder="Offer Title"
                    value={textOffer.title}
                    onChange={handleTextChange}
                  />
                  <input
                    name="discount"
                    placeholder="Discount (%)"
                    value={textOffer.discount}
                    onChange={handleTextChange}
                  />
                  <input
                    name="category"
                    placeholder="Applicable Category"
                    value={textOffer.category}
                    onChange={handleTextChange}
                  />
                  <input
                    name="product"
                    placeholder="Product Name"
                    value={textOffer.product}
                    onChange={handleTextChange}
                  />
                  <select
                    name="festival"
                    value={textOffer.festival}
                    onChange={handleTextChange}
                  >
                    <option value="">Select Festival</option>
                    <option value="Diwali">Diwali</option>
                    <option value="Holi">Holi</option>
                    <option value="Christmas">Christmas</option>
                    <option value="New Year">New Year</option>
                    <option value="Independence Day">Independence Day</option>
                  </select>
                  <textarea
                    name="description"
                    placeholder="Description"
                    value={textOffer.description}
                    onChange={handleTextChange}
                  />

                  <div className="validity-row">
                    <label htmlFor="validFrom">Valid From</label>
                    <input
                      type="date"
                      id="validFrom"
                      name="validFrom"
                      value={textOffer.validFrom}
                      onChange={handleTextChange}
                    />

                    <label htmlFor="validTo">Valid To</label>
                    <input
                      type="date"
                      id="validTo"
                      name="validTo"
                      value={textOffer.validTo}
                      onChange={handleTextChange}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="button-group">
              <button
                className="cancel-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button className="add-btn" onClick={handleAddOffer}>
                Add Offer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOffers;
